import { QueryClient } from '@tanstack/react-query';

// Exponential backoff: 1s, 2s, 4s
const getRetryDelay = (attemptIndex: number): number => {
  // Exponential backoff: 1s, 2s, 4s
  return Math.min(1000 * Math.pow(2, attemptIndex), 4000);
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on client errors (4xx) except 408 (timeout) and 429 (rate limit)
        if (error?.response?.status) {
          const status = error.response.status;
          
          // Don't retry authentication/authorization errors
          if (status === 401 || status === 403) {
            return false;
          }
          
          // Don't retry client errors (4xx) except specific cases
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            return false;
          }
        }
        
        // Retry network errors and server errors (5xx) up to 3 times
        return failureCount < 3;
      },
      retryDelay: getRetryDelay,
      refetchOnWindowFocus: false,
      // Enable refetch on reconnect for better offline support
      refetchOnReconnect: 'always',
      // Network mode: try to fetch from cache if offline
      networkMode: 'offlineFirst',
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Retry mutations only on network errors or 5xx errors
        if (error?.response?.status) {
          const status = error.response.status;
          // Only retry on server errors (5xx) or rate limiting (429)
          if (status >= 500 || status === 429) {
            return failureCount < 2; // Max 2 retries for mutations
          }
          return false;
        }
        // Retry network errors
        return failureCount < 2;
      },
      retryDelay: getRetryDelay,
      // Mutations are online-only by default
      networkMode: 'online',
    },
  },
});